import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteWebsiteThemeById } from 'src/editor/api/website-themes-api'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { useCurrentTheme } from 'src/editor/layout/sidebar/website-styles-settings/hooks/useCurrentTheme'
import { Repeat04Icon } from 'src/editor/shared/icons/repeat-04-icon'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { ThemeSelect } from 'src/editor/shared/ui-lib/theme-select'
import { useWebsiteTheme } from './hooks/useWebsiteTheme'
import { useWebsiteThemes } from './hooks/useWebsiteThemes'

export const WebsiteThemeBase = () => {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchThemes,
  } = useWebsiteThemes()
  const queryClient = useQueryClient()
  const isFirstRender = useRef(true)
  const {
    currentTheme,
    draftTheme,
    activeThemeId,
    setActiveThemeId,
    onReset,
    onUpdate,
    onSelectTheme,
    savedTheme,
  } = useCurrentTheme()

  const {
    refetch,
    isFetching,
    isError: isErrorTheme,
    error: errorTheme,
  } = useWebsiteTheme()

  const handleDelete = async (id: number) => {
    await deleteWebsiteThemeById(id)
    await refetchThemes()
  }

  const handleUpdate = async (id: number | null) => {
    setActiveThemeId(id)

    if (id !== null) {
      queryClient.setQueryData(['activeThemeId'], id)

      const result = await refetch()
      if (result.data) {
        onSelectTheme(result.data)
      }
    }
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    setActiveThemeId(savedTheme?.id ?? null)
    refetchThemes()
  }, [savedTheme])

  return (
    <>
      {(isError || isErrorTheme) && (
        <div className="absolute top-[84px] text-textError">
          Error: {error?.message || errorTheme?.message}
        </div>
      )}
      <GroupTitle
        withoutHorizontalLine
        afterChildren={
          !!draftTheme && (
            <GhostIconButton
              onClick={onReset}
              icon={<Repeat04Icon />}
              className="absolute right-0"
            />
          )
        }
        className="relative mt-9 flex items-center justify-between"
      >
        {t('Theme')}
      </GroupTitle>
      <ThemeSelect
        themes={data?.items}
        activeThemeId={activeThemeId}
        update={handleUpdate}
        onDelete={handleDelete}
        isLoading={isLoading || isFetching}
      />
      {!currentTheme?.isCommon && (
        <TextInputControl
          update={value =>
            onUpdate({
              name: value,
            })
          }
          value={currentTheme?.name ?? ''}
          placeholder={t('website_theme_settings.name_placeholder')}
          required
          wrapperProps={{ labelText: t('website_theme_settings.name') }}
        />
      )}
    </>
  )
}
